import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faPrint,
    faReceipt,
    faPaperPlane,
    faPlug,
    faCog,
} from "@fortawesome/free-solid-svg-icons";
import { Item, StoreDetails, DeliveryCharge, Discount } from "../../types";
import {
    loadTelegramConfig,
    sendReceiptToTelegram,
} from "../../utils/telegram";
import { usePrinter } from "../../contexts/PrinterContext";
import "./PrinterControls.css";

interface PrinterControlsProps {
    items: Item[];
    storeDetails: StoreDetails;
    deliveryCharge: DeliveryCharge;
    discount: Discount;
    disabled: boolean;
    onBeforePrint?: () => void | Promise<void>;
}

const PrinterControls: React.FC<PrinterControlsProps> = ({
    items,
    storeDetails,
    deliveryCharge,
    discount,
    disabled,
    onBeforePrint,
}) => {
    // Use the global printer context instead of local state
    const { 
        printer, 
        printReceipt,
        isPrinting,
        status,
        debugMode,
        debugInfo,
        settings
    } = usePrinter();

    const [errorDetails, setErrorDetails] = useState<string>("");
    const [telegramStatus, setTelegramStatus] = useState<string>("");
    const [, setIsSendingToTelegram] = useState<boolean>(false);

    const handlePrintReceipt = async () => {
        if (items.length === 0) return;

        try {
            setErrorDetails("");

            // Run any pre-print actions (e.g., persisting current UI state)
            if (onBeforePrint) {
                await onBeforePrint();
            }

            // Use the context's printReceipt function which handles all printing logic
            const result = await printReceipt(
                items,
                storeDetails,
                deliveryCharge,
                discount
            );

            if (result.success) {
                // Print was successful
                // Now try to send to Telegram if enabled, but don't interrupt the printing process
                setTimeout(() => {
                    sendToTelegramIfEnabled();
                }, 500); // Small delay to ensure UI updates for print success first
            } else {
                setErrorDetails(
                    result.message || "Unknown error during printing"
                );
            }
        } catch (error) {
            console.error("Error handling print receipt:", error);
            setErrorDetails(
                error instanceof Error ? error.message : String(error)
            );
        }
    };

    // Helper function to send receipt to Telegram if enabled
    const sendToTelegramIfEnabled = async () => {
        // Load the latest Telegram config
        const telegramConfig = loadTelegramConfig();

        // Check if Telegram integration is enabled
        if (
            !telegramConfig.enabled ||
            !telegramConfig.botToken ||
            !telegramConfig.chatId
        ) {
            return;
        }

        try {
            setIsSendingToTelegram(true);
            setTelegramStatus("Sending receipt to Telegram...");

            const result = await sendReceiptToTelegram(
                items,
                storeDetails,
                deliveryCharge,
                discount
            );

            if (result.success) {
                setTelegramStatus("Receipt sent to Telegram successfully");

                // Clear status after a few seconds
                setTimeout(() => {
                    setTelegramStatus("");
                }, 5000);
            } else {
                setTelegramStatus(
                    `Failed to send to Telegram: ${result.message}`
                );
            }
        } catch (error) {
            console.error("Error sending to Telegram:", error);
            setTelegramStatus(
                `Error: ${
                    error instanceof Error ? error.message : "Unknown error"
                }`
            );
        } finally {
            setIsSendingToTelegram(false);
        }
    };

    return (
        <div className="printer-controls-container">
            <div className="print-header">
                <h2>Print Receipt</h2>
                <a
                    href="/settings"
                    className="settings-link"
                    title="Printer Settings"
                >
                    <FontAwesomeIcon icon={faCog} />
                </a>
            </div>

            <div className="printer-status-bar">
                <div
                    className={`status-indicator ${
                        printer ? "connected" : "disconnected"
                    }`}
                >
                    <FontAwesomeIcon icon={faPlug} />{" "}
                    {printer ? "Connected" : "Not Connected"}
                </div>
                {status && <div className="status-message">{status}</div>}
                {errorDetails && (
                    <div className="error-details">{errorDetails}</div>
                )}
            </div>

            <div className="print-actions">
                <button
                    className="print-button primary"
                    onClick={handlePrintReceipt}
                    disabled={disabled || isPrinting || items.length === 0}
                >
                    <FontAwesomeIcon
                        icon={
                            settings.defaultPrintMethod === "thermal"
                                ? faReceipt
                                : faPrint
                        }
                    />
                    {isPrinting ? "Printing..." : "Print Receipt"}
                </button>

                <div className="print-method-info">
                    <p>
                        Using{" "}
                        {settings.defaultPrintMethod === "thermal"
                            ? "Thermal Printer"
                            : "Browser Print"}
                    </p>
                    <p className="settings-note">
                        Change default print method in Settings
                    </p>
                </div>
            </div>

            {telegramStatus && (
                <div className="telegram-status">
                    <div className="status-message">
                        <FontAwesomeIcon icon={faPaperPlane} /> {telegramStatus}
                    </div>
                </div>
            )}

            {debugMode && debugInfo && (
                <div className="debug-info">
                    <h3>Debug Information</h3>
                    <pre>{debugInfo}</pre>
                </div>
            )}
        </div>
    );
};

export default PrinterControls;
